import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowRight, FileDown, TrendingUp, Target, AlertCircle, Activity, Zap, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { PowerBESSLogo } from "../components/PowerBESSLogo";
import ForecastChart from "../components/ForecastChart";
import ErrorBoundary from "../components/ErrorBoundary";
import {
  useForecastIndex, useForecastDay, useAllSettledDays, fetchDays,
} from "../lib/forecastApi";
import { exportWeeklyPDF } from "../lib/weeklyPdfExport";
import { exportDayPDF } from "../lib/dayPdfExport";
import "../styles/ForecastAccuracy.css";

// ─── Forecast data is published as static JSON to the CDN by an EKS job (blind
// walk-forward history of settled days only). The page is back-dated — no live
// T+1 pill — and the picker walks a week at a time. ──────────────────────────

// Earliest settled day the page is allowed to surface.
const MIN_DATE = "2026-05-01";
const fmt = (n, d = 1) => (n === null || n === undefined ? "—" : Number(n).toFixed(d));
const fmtSigned = (n) => (n === null || n === undefined ? "—" : (n >= 0 ? "+" : "") + Math.round(n));
// "Mon 1 Jun" → "1 Jun"
const stripDow = (label) => label.split(" ").slice(1).join(" ");

export default function ForecastAccuracy() {
  const { data: index, error: indexErr, isLoading: indexLoading } = useForecastIndex();
  const months = useMemo(() => index?.months?.map((m) => m.month) || [], [index]);
  const { data: allDays } = useAllSettledDays(months);

  // Settled days only, no earlier than MIN_DATE, oldest→newest.
  const settledDays = useMemo(
    () => (allDays || []).filter((d) => d.date >= MIN_DATE),
    [allDays],
  );

  // Slice into 7-day windows ending on the latest settled day. weeks[0] is the
  // most recent week; the oldest may be a partial (<7) trailing block.
  const weeks = useMemo(() => {
    const out = [];
    for (let i = settledDays.length; i > 0; i -= 7) {
      out.push(settledDays.slice(Math.max(0, i - 7), i));
    }
    return out;
  }, [settledDays]);

  const [weekIndex, setWeekIndex] = useState(0);
  useEffect(() => {
    if (weeks.length && weekIndex >= weeks.length) setWeekIndex(0);
  }, [weeks, weekIndex]);

  const weekDays = useMemo(() => weeks[weekIndex] || [], [weeks, weekIndex]);
  const weekRange = useMemo(() => {
    if (!weekDays.length) return "";
    const yr = weekDays[0].date.slice(0, 4);
    return `${stripDow(weekDays[0].label)} – ${stripDow(weekDays[weekDays.length - 1].label)} ${yr}`;
  }, [weekDays]);

  // Selected day defaults to the latest day in the visible week.
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    if (weekDays.length && (selected === null || !weekDays.some((d) => d.date === selected))) {
      setSelected(weekDays[weekDays.length - 1].date);
    }
  }, [weekDays, selected]);

  const { data: dayDoc } = useForecastDay(selected);

  const chartBlocks = dayDoc?.blocks || [];
  const day = useMemo(() => {
    const summary = dayDoc?.summary || weekDays.find((d) => d.date === selected) || null;
    const meta = dayDoc || weekDays.find((d) => d.date === selected) || {};
    return summary ? { date: meta.date, dow: meta.dow, label: meta.label, ...summary } : null;
  }, [dayDoc, weekDays, selected]);

  // Hero stats from the manifest rollups (volume-weighted, server-computed).
  const weekStats = index?.rolling7 || null;
  const best = index?.allTime?.best || (settledDays.length
    ? settledDays.reduce((a, b) => ((b.accuracy ?? 0) > (a.accuracy ?? 0) ? b : a))
    : null);

  // ── PDF export ────────────────────────────────────────────────────────────
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ pct: 0, label: "" });
  const [exportingDay, setExportingDay] = useState(false);
  const [dayExportProgress, setDayExportProgress] = useState({ pct: 0, label: "" });
  const exportMountRef = useRef(null);

  const handleExportDay = useCallback(async () => {
    if (exportingDay || !dayDoc) return;
    setExportingDay(true);
    setDayExportProgress({ pct: 0, label: "Preparing…" });
    try {
      const { pdf, fileName } = await exportDayPDF({
        forecastData: { [day.date]: dayDoc.blocks },
        day: { id: 0, date: day.date, dow: day.dow, label: day.label },
        mountNode: exportMountRef.current || document.body,
        onProgress: (pct, label) => setDayExportProgress({ pct, label }),
      });
      pdf.save(fileName);
    } catch (e) {
      console.error("Day PDF export failed", e);
      alert("PDF export failed. Please try again or check the browser console.");
    } finally {
      setTimeout(() => { setExportingDay(false); setDayExportProgress({ pct: 0, label: "" }); }, 600);
    }
  }, [exportingDay, dayDoc, day]);

  const handleExportWeekly = useCallback(async () => {
    if (exporting || !weekDays.length) return;
    setExporting(true);
    setExportProgress({ pct: 0, label: "Fetching days…" });
    try {
      const docs = await fetchDays(weekDays.map((d) => d.date));
      const forecastData = {};
      const days = docs.map((doc, i) => {
        forecastData[doc.date] = doc.blocks;
        return { id: i, date: doc.date, dow: doc.dow, label: doc.label };
      });
      const { pdf, fileName } = await exportWeeklyPDF({
        forecastData, days,
        mountNode: exportMountRef.current || document.body,
        onProgress: (pct, label) => setExportProgress({ pct, label }),
      });
      pdf.save(fileName);
    } catch (e) {
      console.error("Weekly PDF export failed", e);
      alert("PDF export failed. Please try again or check the browser console.");
    } finally {
      setTimeout(() => { setExporting(false); setExportProgress({ pct: 0, label: "" }); }, 600);
    }
  }, [exporting, weekDays]);

  // ── Week navigation: lower weekIndex = newer week ──────────────────────────
  const gotoOlder = () => {
    if (weekIndex < weeks.length - 1) { setWeekIndex(weekIndex + 1); setSelected(null); }
  };
  const gotoNewer = () => {
    if (weekIndex > 0) { setWeekIndex(weekIndex - 1); setSelected(null); }
  };

  const region = index?.asset?.region || "IEX DAM";

  // ── States ──────────────────────────────────────────────────────────────────
  if (indexLoading) return <FullStatus icon={<Loader2 className="fa-spin" size={26} />} title="Loading live forecast accuracy…" />;
  if (indexErr || !index) return <FullStatus title="Live data is updating" sub="Our latest forecast-accuracy report is being published. Please check back in a few minutes." />;

  return (
    <div className="fa-wrap" data-testid="forecast-accuracy-page">
      <Helmet>
        <title>Forecast Accuracy · PowerBESS vs IEX DAM</title>
        <meta name="description" content="Day-by-day comparison of PowerBESS blind day-ahead forecasts against actual IEX DAM clearing prices. Hourly resolution, full error analysis, updated daily." />
        <link rel="canonical" href="https://powerbess.ai/forecast-accuracy" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="PowerBESS vs IEX DAM — forecast accuracy" />
        <meta property="og:description" content="Real prices. Blind forecasts. Zero hindsight. See how PowerBESS performs against actual IEX DAM clearing prices, updated daily." />
        <meta property="og:url" content="https://powerbess.ai/forecast-accuracy" />
      </Helmet>

      {/* ─── Header ─── */}
      <header className="fa-header" data-testid="fa-header">
        <Link to="/" className="fa-logo" data-testid="fa-logo">
          <PowerBESSLogo size={28} />
          <span>Power<span className="fa-a">BESS</span>.ai</span>
        </Link>
        <Link to="/" className="fa-back" data-testid="fa-back">
          <ArrowLeft size={14} /> Back to site
        </Link>
      </header>

      {/* ─── Hero ─── */}
      <section className="fa-hero" data-testid="fa-hero">
        <div className="fa-hero-tag">BLIND BACKTEST · IEX DAM · {region.toUpperCase()}</div>
        <h1 className="fa-hero-title">
          PowerBESS forecast <span className="fa-h-vs">vs</span> IEX DAM clearing price.
        </h1>
        <p className="fa-hero-sub">
          Hourly resolution, updated daily. Blind walk-forward forecasts published before
          the day-ahead auction closed — then scored against the actual price India paid.
          No hindsight. No tuning. No selection bias.
        </p>

        <div className="fa-hero-stats" data-testid="fa-week-stats">
          <Stat label="7-day accuracy" value={`${fmt(weekStats?.accuracy, 1)}%`} sub="vol-weighted · 100−WMAPE" tone="lime" />
          <Stat label="7-day WMAPE" value={`${fmt(weekStats?.wmape, 2)}%`} sub="volume-weighted" />
          <Stat label="P10−P90 cover" value={`${fmt(weekStats?.coverage, 0)}%`} sub="target 80%" tone={(weekStats?.coverage ?? 0) >= 80 ? "lime" : "amber"} />
          <Stat label="Best day" value={`${fmt(best?.accuracy, 1)}%`} sub={best?.label || "—"} />
        </div>
      </section>

      {/* ─── Day picker (week navigator + pills) ─── */}
      <section className="fa-picker-wrap">
        <div className="fa-picker-head">
          <div className="fa-picker-l">SELECT A DAY</div>
          <div className="fa-nav" data-testid="fa-week-nav">
            <button className="fa-nav-btn" onClick={gotoOlder} disabled={weekIndex >= weeks.length - 1} aria-label="Previous week"><ChevronLeft size={16} /></button>
            <span className="fa-nav-month fa-nav-week" aria-live="polite">{weekRange || "—"}</span>
            <button className="fa-nav-btn" onClick={gotoNewer} disabled={weekIndex <= 0} aria-label="Next week"><ChevronRight size={16} /></button>
          </div>
        </div>
        <div className="fa-picker" role="tablist" data-testid="fa-day-picker">
          {weekDays.map((d) => (
            <button
              key={d.date} role="tab" aria-selected={d.date === selected}
              className={`fa-pill ${d.date === selected ? "is-active" : ""}`}
              onClick={() => setSelected(d.date)} data-testid={`fa-day-${d.date}`}
            >
              <span className="fa-pill-dow">{d.dow}</span>
              <span className="fa-pill-num">{Number(d.date.slice(8))}</span>
              <span className="fa-pill-acc">{fmt(d.accuracy, 1)}%</span>
            </button>
          ))}
        </div>
      </section>

      {/* ─── Selected day: KPIs + chart ─── */}
      <section className="fa-day" data-testid="fa-day-view">
        {!day ? (
          <div className="fa-day-loading"><Loader2 className="fa-spin" size={20} /> Loading…</div>
        ) : (
          <>
            <div className="fa-day-head">
              <div>
                <div className="fa-day-eyebrow">{day.dow.toUpperCase()} · BLIND DAY-AHEAD FORECAST</div>
                <h2 className="fa-day-title">{day.label}, {day.date.slice(0, 4)}</h2>
              </div>
              <div className="fa-day-acc">
                <span className="fa-day-acc-v">{fmt(day.accuracy, 1)}<i>%</i></span>
                <span className="fa-day-acc-l">Accuracy</span>
              </div>
            </div>

            <div className="fa-kpis" data-testid="fa-kpis">
              <Kpi icon={<Target size={14} />} label="Accuracy" value={`${fmt(day.accuracy, 1)}%`} note="100 − WMAPE" tone="lime" />
              <Kpi icon={<Activity size={14} />} label="WMAPE" value={`${fmt(day.wmape, 2)}%`} note="volume-weighted" />
              <Kpi icon={<TrendingUp size={14} />} label="MAPE" value={`${fmt(day.mape, 1)}%`} note="block-mean" />
              <Kpi icon={<Zap size={14} />} label="Mean abs error" value={`₹ ${Math.round(day.mae)}`} note="per MWh" />
              <Kpi icon={<AlertCircle size={14} />} label="P10−P90 cover" value={`${fmt(day.coverage, 0)}%`} note="target 80%" tone={day.coverage >= 80 ? "lime" : "amber"} />
              <Kpi label="Bias" value={fmtSigned(day.bias)} note={day.bias >= 0 ? "over-forecast (Rs/MWh)" : "under-forecast (Rs/MWh)"} tone={Math.abs(day.bias) > 400 ? "amber" : "neutral"} />
            </div>

            <div className="fa-chart-card" data-testid="fa-chart-card">
              <div className="fa-chart-head">
                <div>
                  <div className="fa-chart-eyebrow">PRICE COMPARISON · PER-BLOCK ERROR</div>
                  <h3 className="fa-chart-title">Hourly view — Forecast vs Actual</h3>
                </div>
                <div className="fa-chart-dl-group" data-testid="fa-chart-dl-group">
                  <button type="button" className="fa-chart-dl fa-chart-dl-primary" onClick={handleExportDay}
                    disabled={exportingDay || exporting || !dayDoc}
                    data-testid="fa-export-day-pdf" aria-label={`Download ${day.label} report as PDF`}>
                    {exportingDay ? (<><Loader2 size={14} className="fa-spin" /> {dayExportProgress.label || "Exporting…"} {dayExportProgress.pct ? `(${Math.round(dayExportProgress.pct)}%)` : ""}</>)
                      : (<><FileDown size={14} /> Download {day.label.split(" ").slice(0, 2).join(" ")} (PDF)</>)}
                  </button>
                  <button type="button" className="fa-chart-dl" onClick={handleExportWeekly} disabled={exporting || exportingDay}
                    data-testid="fa-export-weekly-pdf" aria-label="Export weekly report as PDF">
                    {exporting ? (<><Loader2 size={14} className="fa-spin" /> {exportProgress.label || "Exporting…"} {exportProgress.pct ? `(${Math.round(exportProgress.pct)}%)` : ""}</>)
                      : (<><FileDown size={14} /> Weekly report (PDF)</>)}
                  </button>
                </div>
              </div>
              <div className="fa-chart-img-wrap">
                {chartBlocks.length ? (
                  <ErrorBoundary>
                    <ForecastChart data={chartBlocks} dayLabel={day.label} height={420} />
                  </ErrorBoundary>
                ) : <div className="fa-day-loading"><Loader2 className="fa-spin" size={20} /> Loading chart…</div>}
              </div>
              <div className="fa-chart-legend">
                <span className="fa-leg"><i className="fa-leg-line fa-leg-actual" /> Actual IEX DAM</span>
                <span className="fa-leg"><i className="fa-leg-line fa-leg-forecast" /> PowerBESS p50</span>
                <span className="fa-leg"><i className="fa-leg-band" /> P10−P90 band</span>
                <span className="fa-leg"><i className="fa-leg-dot fa-leg-green" /> APE ≤ 5%</span>
                <span className="fa-leg"><i className="fa-leg-dot fa-leg-amber" /> APE ≤ 15%</span>
                <span className="fa-leg"><i className="fa-leg-dot fa-leg-red" /> APE &gt; 15%</span>
              </div>
            </div>
          </>
        )}
      </section>

      {/* ─── Week table ─── */}
      <section className="fa-table-section" data-testid="fa-table-section">
        <div className="fa-table-head">
          <h3 className="fa-table-title">{weekRange ? `Week of ${weekRange} at a glance` : "Week at a glance"}</h3>
          <p className="fa-table-sub">Every settled day in this week. Click a row to jump to its chart. Use the arrows above to change week.</p>
        </div>
        <div className="fa-table-wrap">
          <table className="fa-table">
            <thead>
              <tr>
                <th>Date</th>
                <th className="fa-num">Accuracy</th>
                <th className="fa-num">WMAPE</th>
                <th className="fa-num">MAPE</th>
                <th className="fa-num">MAE (₹/MWh)</th>
                <th className="fa-num">P10−P90</th>
                <th className="fa-num">Bias</th>
              </tr>
            </thead>
            <tbody>
              {weekDays.map((d) => (
                <tr key={d.date} className={d.date === selected ? "is-selected" : ""}
                  onClick={() => { setSelected(d.date); window.scrollTo({ top: document.querySelector(".fa-day").offsetTop - 70, behavior: "smooth" }); }}
                  data-testid={`fa-row-${d.date}`}>
                  <td><span className="fa-td-dow">{d.dow}</span> {d.label.split(" ").slice(1).join(" ")}</td>
                  <td className={`fa-num fa-acc-cell ${d.accuracy >= 90 ? "is-lime" : d.accuracy >= 85 ? "is-amber" : "is-red"}`}>{fmt(d.accuracy, 1)}%</td>
                  <td className="fa-num">{fmt(d.wmape, 2)}%</td>
                  <td className="fa-num">{fmt(d.mape, 1)}%</td>
                  <td className="fa-num">₹ {Math.round(d.mae)}</td>
                  <td className={`fa-num ${d.coverage >= 80 ? "is-lime" : "is-amber"}`}>{fmt(d.coverage, 0)}%</td>
                  <td className={`fa-num ${d.bias >= 0 ? "is-pos" : "is-neg"}`}>{fmtSigned(d.bias)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ─── Methodology ─── */}
      <section className="fa-meth" data-testid="fa-methodology">
        <h3 className="fa-meth-title">How these numbers were produced</h3>
        <div className="fa-meth-grid">
          <Method n="01" title="Forecast horizon" body="Day-ahead, 96 blocks of 15 minutes each. Published before the IEX DAM auction window closes." />
          <Method n="02" title="Engine" body="gbm2 — gradient-boosted forecast stack with quantile heads. No proprietary IEX data leaks. Walk-forward only." />
          <Method n="03" title="Score" body="WMAPE = Σ|forecast − actual| / Σ|actual|, weighted by block volume. Accuracy = 100 − WMAPE." />
          <Method n="04" title="No selection" body="Every settled day appears here — updated daily. No cherry-picking, good days and bad." />
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="fa-cta" data-testid="fa-cta">
        <div className="fa-cta-inner">
          <h3 className="fa-cta-title">Want this report on <span className="fa-cta-em">your</span> site&apos;s data?</h3>
          <p className="fa-cta-sub">We&apos;ll run PowerBESS against the last 30–90 days of your local IEX node and ship a custom accuracy + arbitrage backtest within 48 hours.</p>
          <Link to="/backtest-request" className="fa-cta-btn" data-testid="fa-cta-btn">
            Request a free backtest <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      <footer className="fa-footer">
        <span>© 2026 PowerBESS.ai — gbm2 engine · IEX DAM {region} · blind day-ahead</span>
        <Link to="/" className="fa-foot-link">powerbess.ai</Link>
      </footer>

      {/* Hidden mount-point for off-screen chart rendering during PDF export */}
      <div ref={exportMountRef} aria-hidden="true" style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none", width: 0, height: 0, overflow: "hidden" }} />
    </div>
  );
}

/* ─── Sub-components ─── */
function FullStatus({ icon, title, sub }) {
  return (
    <div className="fa-wrap" data-testid="forecast-accuracy-page">
      <header className="fa-header">
        <Link to="/" className="fa-logo"><PowerBESSLogo size={28} /><span>Power<span className="fa-a">BESS</span>.ai</span></Link>
        <Link to="/" className="fa-back"><ArrowLeft size={14} /> Back to site</Link>
      </header>
      <div className="fa-fullstatus" data-testid="fa-status">
        {icon}
        <h1 className="fa-fullstatus-t">{title}</h1>
        {sub && <p className="fa-fullstatus-s">{sub}</p>}
      </div>
    </div>
  );
}

function Stat({ label, value, sub, tone = "neutral" }) {
  return (
    <div className={`fa-stat fa-tone-${tone}`}>
      <div className="fa-stat-v">{value}</div>
      <div className="fa-stat-l">{label}</div>
      <div className="fa-stat-s">{sub}</div>
    </div>
  );
}

function Kpi({ icon, label, value, note, tone = "neutral" }) {
  return (
    <div className={`fa-kpi fa-tone-${tone}`}>
      <div className="fa-kpi-head">
        {icon && <span className="fa-kpi-i">{icon}</span>}
        <span className="fa-kpi-l">{label}</span>
      </div>
      <div className="fa-kpi-v">{value}</div>
      <div className="fa-kpi-n">{note}</div>
    </div>
  );
}

function Method({ n, title, body }) {
  return (
    <div className="fa-meth-card">
      <div className="fa-meth-n">{n}</div>
      <div className="fa-meth-t">{title}</div>
      <p className="fa-meth-b">{body}</p>
    </div>
  );
}
